import { FC, useEffect, useMemo, useState, useCallback } from 'react';

import classNames from 'clsx';

import { getQuery } from '../../utilities/getQuery';
import { BarChart } from '../../components/BarChart/BarChart';
import { PermissionCheck } from '../../components/PermissionCheck';
import { MultiFilter } from '../../components/MultiFilter/MultiFilter';

import { routesPath } from '../../constants/routes';
import { PageTitle } from '../../components/PageTitle';

import { ChartViewTabs } from '../../components/ChartViewTabs';
import { useMultiFilterStore } from '../../components/MultiFilter/MultiFilter.store';
import { ChartViewSwitch } from '../../components/ChartViewSwitch/ChartViewSwitch';
import { BarChartReportSkeleton } from '../../components/BarChartReportSkeleton/BarChartReportSkeleton';
import { ReportFilterSlot } from '../../components/ReportFilterSlot/ReportFilterSlot';
import {
  ChartReportFooterLabels,
  ModalityLegend,
} from '../../components/ChartReportFooterLabels/ChartReportFooterLabels';
import { useControls, tableId } from './useControls';
import { generateXLSX, GenesisTable } from '../../components/GenesisTable/GenesisTable';
import { TemporalActivitiesLabels } from '../../constants/analyticsLabels';
import { useBarChartEngine } from '../../components/BarChart/useBarChartEngine';
import {
  DisplayOptionValue,
  displayOptions,
  peopleCountDisplayOptions,
} from '../genesis-analytics/useControls';
import { useAppContext } from '../../context/appContext';
import { makeQuery } from '../../utilities/helpers';
import { capitalize, debounce } from 'lodash';
import { Chart } from 'chart.js';
import { DataPoint } from '../../components/DataPointTooltip/DataPointTooltip';
import { useQueryCase } from '../../rest/query/useQueryCase';
import { useHistory } from '../../hooks/useHistory';
import { PreviewModal } from '../../components/PreviewModal';
import { useQueryUtilization } from '../../rest/query/useQueryUtilization';
import { utilizationOptions } from '../../components/MultiFilter/mocks';
import { ChartViewButtons } from '../../components/ChartViewButtons/ChartViewButtons';
import { DownloadButton } from '../../components/DownloadButton';

const chartId = 'utilization-chart';

const REPORT_NAME = 'utilization';
const REPORT_TITLE = 'Utilization';

export const Utilization: FC = () => {
  const history = useHistory();
  const dateRanges = getQuery(history);
  const { sidebarCollapsed } = useAppContext();
  const multiFilterStore = useMultiFilterStore();
  const filtersState = multiFilterStore[REPORT_NAME];
  const [activityGroups, setActivityGroups] = useState<
    Record<ActivityGroupKeys, string[]> | undefined
  >();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openedDataPoint, setOpenedDataPoint] = useState<DataPoint | undefined>();
  const [isPreview, setIsPreview] = useState(false);

  const {
    displayOptionList,
    compareBy,
    downloadChart,
    copyToClipboard,
    // setCompareBy,
    switchChartViews,
    view,
    onViewChange,
  } = useControls();

  const currentQuery = useMemo(
    () => makeQuery(dateRanges, filtersState),
    [dateRanges, filtersState],
  );

  const { data: sourceData, rawData, allFilters, loading } = useQueryUtilization(currentQuery);

  const caseId = openedDataPoint?.point.case_id;

  const { meta: caseData, loading: caseLoading } = useQueryCase(String(caseId), !!caseId);

  const surgeons = allFilters.surgeons;

  const { filtersBar, panel, selectedFilters } = MultiFilter({
    reportName: REPORT_NAME,
    drawerOpen,
    hideFilters: ['surgeons'],
    isLoading: loading,
    customCompareOptions: utilizationOptions.compareOptions,
    customCategoryOptions: utilizationOptions.categoryOptions,
    hideCategoryDropdown: false,
    hideCompareSelectbox: false,
    hideStackByDropdown: false,
    hideNormalizationDropdown: false,
    allFilters,
    availableFilters: rawData?.available_filters,
    // withSwitch: true,
    // activitiesViews: activityGroupsOptions,
    rightFiltersBarSlot: (
      <ChartViewTabs
        enabledList={['Bar View', 'List View']}
        defaultItem={view || 'Bar View'}
        onChange={onViewChange}
      />
    ),
    onOpenPanel: () => setDrawerOpen(true),
    onClosePanel: () => setDrawerOpen(false),
  });

  const openDataPoint = debounce((point: ChartDataPoint, chart: Chart) => {
    setOpenedDataPoint({ point, chart });
  }, 500);

  const getYAxisLabel = () => {
    if (filtersState.normalization == null) return 'Hours';
    if (filtersState.normalization == 'adjusted_prime_time')
      return 'Adjusted Prime Time Utilization ';
    if (filtersState.normalization == 'prime_time') return 'Prime Time Utilization ';
    return '';
  };

  const { barChartOptions, getBarChartData } = useBarChartEngine({
    // compareBy:
    compareBy: 'modality',
    displayOptionList,
    sourceData,
    unit: filtersState.normalization == null ? ' Hours' : '%',
    isUtilizationChart: true,
    // responsive: true,
    // maintainAspectRatio: false,
    transformKeys: (
      val,
      significant, //val,
    ) =>
      // TemporalActivitiesLabels[val] +
      val +
      (significant !== null && displayOptionList.includes('statisticalSignificance')
        ? ` (${significant ? 'sig' : 'insig'})`
        : ''),
    allFilters,
    extendOptions: {
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          beginAtZero: true,
          // min: 51,
          // max: 100,
          title: {
            text: getYAxisLabel(),
          },
          ticks: {
            stepSize: 5,
            callback: (v) => v + (filtersState.normalization == null ? ' Hours' : '%'),
          },
        },
      },
      plugins: {
        clickOnTick: {
          x: {
            enabled: false,
            onClick: (label, b, datasets: any[]) => {
              const dataset = datasets?.[0];

              const x = dataset?.phaseNames?.[label?.index as number];
              if (label?.scaleId === 'x' && x) {
                onViewChange('Bubble View');
                // onPhaseChange(x);
              }
            },
          },
          y: {
            enabled: false,
          },
        },
        datalabels: {
          labels: {
            value: {
              // offset: -30,
              // align: 'bottom',
            },
          },
        },
      },
    },
  });

  const downloadXLSX = useCallback(() => {
    generateXLSX({
      compareBy,
      allFilters,
      tableId,
      sourceData,
      labels: TemporalActivitiesLabels,
    });
  }, [sourceData, surgeons, compareBy]);

  useEffect(() => {
    if (activityGroups || !rawData?.activity_group) return;
    setActivityGroups(rawData?.activity_group);
  }, [rawData?.activity_group, activityGroups]);

  const renderBarChart = () => (
    <BarChart
      id={chartId}
      key={sidebarCollapsed ? 'collapsed' : 'expanded'}
      isPreview={isPreview}
      chartTitle={REPORT_TITLE}
      data={getBarChartData() as any}
      isUtilizationChart={true}
      filterSlot={
        <ReportFilterSlot<DisplayOptionValue>
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          displayOptions={displayOptions.map((d) => ({
            ...d,
            label: d.value === 'dataPoints' && filtersState.excludeOutliers ? 'Outliers' : d.label,
            disabled: d.value === 'dataPoints',
            hidden: d.value === 'dataPoints',
            //d.value === 'statisticalSignificance' && !compareBy ,
          }))}
          hideOptions={false} //{compareBy === 'national_statistics'}
          onSwitchChartViews={switchChartViews}
          displayOptionList={displayOptionList}
          selectedFilters={selectedFilters}
          onDownloadChart={downloadChart}
          onCopyToClipboard={copyToClipboard}
        />
      }
      options={barChartOptions}
      footer={
        <ChartReportFooterLabels
          compareBy={compareBy}
          sourceData={sourceData}
          allFilters={allFilters}
          isUtilizationChart={true}
        />
      }
      sidebarLegend={
        <ModalityLegend
          filterState={filtersState}
          multiFilterStore={multiFilterStore}
          isPreview={isPreview}
        />
      }
    />
  );

  const renderList = () => (
    <div>
      <div className="flex justify-between items-center">
        <div className="p-10">{selectedFilters}</div>
        <div>
          {/* className={classNames('mr-10')}className={classNames('mr-10')} */}
          <div className="flex justify-between">
            <ChartViewButtons
              displayOptionList={displayOptionList}
              displayOptions={peopleCountDisplayOptions
                .map((d) => ({
                  ...d,
                  disabled: d.value === 'statisticalSignificance' && !compareBy,
                }))
                .filter((d) => ['statisticalSignificance', 'sampleSize'].includes(d.value))}
              onSwitchChartViews={switchChartViews}
            />
            <DownloadButton
              title="Download Excel"
              onClick={downloadXLSX}
              extraClassNames="-mt-[1px] pt-2 mr-4"
            />
          </div>
        </div>
      </div>
      <div
        className={classNames('whitespace-nowrap overflow-auto transition-all', {
          'w-[calc(100vw-200px)]': sidebarCollapsed,
          'w-[calc(100vw-320px)]': !sidebarCollapsed,
        })}
      >
        <GenesisTable<GenesisKeys>
          tableId={tableId}
          unit="p"
          showSize={displayOptionList.includes('sampleSize')}
          showMin={displayOptionList.includes('min')}
          showMax={displayOptionList.includes('max')}
          loading={loading}
          transformKeys={(val, significant) =>
            val +
            (significant !== null && displayOptionList.includes('statisticalSignificance')
              ? ` (${significant ? 'sig' : 'insig'})`
              : '')
          }
          data={sourceData}
          compareBy={compareBy}
          allFilters={allFilters}
          isUtilizationChart={true}
        />
      </div>
    </div>
  );

  return (
    <PermissionCheck check="readAnalytics" fallback={<div />}>
      <PageTitle
        header={REPORT_TITLE}
        crumbs={[{ title: 'Analytics', url: routesPath.analytics.replace('/:refId', '') }]}
      />

      <div className="relative">
        {panel}
        {filtersBar}
        <div className="bg-white shadow-sm mt-10">
          {!isPreview && (
            <ChartViewSwitch
              skeleton={<BarChartReportSkeleton />}
              view={view}
              defaultView="Bar View"
              loading={loading}
              bar={renderBarChart}
              list={renderList}
            />
          )}
          {isPreview && (
            <PreviewModal isOpen={true} onClose={() => setIsPreview(false)}>
              <div className="p-6 px-2 h-full pb-10 bg-white">
                <div className="bg-white shadow-sm h-full">
                  {
                    <ChartViewSwitch
                      skeleton={<BarChartReportSkeleton />}
                      view={view}
                      defaultView="Bar View"
                      loading={loading}
                      bar={renderBarChart}
                    />
                  }
                </div>
              </div>
            </PreviewModal>
          )}
        </div>
      </div>
    </PermissionCheck>
  );
};
